/**
 * Plan Lock/Unlock Interface
 * Lock approved plans and manage unlock requests
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlanLockUnlock = () => {
  const { planId } = useParams();
  const [plan, setPlan] = useState(null);
  const [lockInfo, setLockInfo] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');

  useEffect(() => {
    if (planId) {
      checkLockStatus();
      fetchLockInfo();
    }
  }, [planId]);

  const checkLockStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/plan-locks/${planId}/is-locked`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsLocked(response.data.is_locked);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchLockInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/plan-locks/${planId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLockInfo(response.data);
    } catch (error) {
      console.log('No lock info');
    }
  };

  const lockPlan = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/plan-locks`,
        {
          plan_id: planId,
          lock_reason: 'Plan approved and finalized'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Plan locked successfully');
      checkLockStatus();
      fetchLockInfo();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const requestUnlock = async () => {
    if (!unlockReason || unlockReason.length < 10) {
      alert('Please provide a reason (minimum 10 characters)');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/plan-locks/unlock-request`,
        {
          plan_id: planId,
          reason: unlockReason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Unlock request submitted. Awaiting supervisor approval.');
      setUnlockReason('');
      fetchLockInfo();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Plan Lock Management</h1>

      {/* Lock Status Card */}
      <div className={`rounded-lg shadow-lg p-8 mb-6 ${
        isLocked ? 'bg-red-50 border-2 border-red-500' : 'bg-green-50 border-2 border-green-500'
      }`}>
        <div className="text-center">
          <div className="text-6xl mb-4">{isLocked ? '🔒' : '🔓'}</div>
          <h2 className="text-2xl font-bold mb-2">
            Plan is {isLocked ? 'LOCKED' : 'UNLOCKED'}
          </h2>
          <p className="text-gray-600">
            {isLocked 
              ? 'This plan cannot be edited. Request unlock if changes needed.'
              : 'This plan can be edited. Lock it after approval to prevent changes.'}
          </p>
        </div>
      </div>

      {/* Lock Info */}
      {lockInfo && isLocked && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">Lock Information</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Locked By:</span>
              <span className="font-medium">{lockInfo.locked_by?.substring(0, 20)}...</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Locked At:</span>
              <span className="font-medium">{new Date(lockInfo.locked_at).toLocaleString()}</span>
            </div>
            {lockInfo.lock_reason && (
              <div className="flex justify-between">
                <span className="text-gray-600">Reason:</span>
                <span className="font-medium">{lockInfo.lock_reason}</span>
              </div>
            )}
            
            {lockInfo.unlock_requested && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <div className="font-bold text-yellow-800 mb-1">⏳ Unlock Request Pending</div>
                <div className="text-xs text-yellow-700">
                  Requested by: {lockInfo.unlock_requested_by?.substring(0, 20)}...
                </div>
                <div className="text-xs text-yellow-700">
                  Reason: {lockInfo.unlock_request_reason}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold mb-4">Actions</h3>
        
        {!isLocked && (
          <button
            onClick={lockPlan}
            className="w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 font-medium"
          >
            🔒 Lock Plan (Prevent Editing)
          </button>
        )}
        
        {isLocked && !lockInfo?.unlock_requested && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Reason for Unlock Request *</label>
              <textarea
                value={unlockReason}
                onChange={(e) => setUnlockReason(e.target.value)}
                placeholder="Explain why the plan needs to be unlocked..."
                className="w-full border rounded-lg px-4 py-2 h-24"
              />
            </div>
            <button
              onClick={requestUnlock}
              className="w-full bg-yellow-600 text-white py-3 rounded-lg hover:bg-yellow-700 font-medium"
            >
              🔓 Request Unlock (Requires Approval)
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          ℹ️ <strong>Maker-Checker:</strong> Locked plans ensure data integrity. 
          Unlock requests require supervisor approval and create audit trail.
        </p>
      </div>
    </div>
  );
};

export default PlanLockUnlock;